// Type definition for image size config
export interface ImageSizeConfig {
  thumbnail: number;
  small: number;
  medium: number;
  large: number;
  main: number;
}

// Supabase Storage's plain object-serving endpoint always sends Cache-Control: no-cache
// regardless of the object's own stored cacheControl metadata (confirmed live, not fixable
// via re-upload). image-proxy-worker (Cloudflare Worker, routed at img.veevoerwinkel.co.za/*
// via a CNAME, matching the working feed.veevoerwinkel.co.za pattern) proxies the same plain
// endpoint and sets a real Cache-Control + edge-caches the response — this just rewrites the
// URL to go through it. Absolute URL (not a relative path) is deliberate: Worker routes match
// on the requested URL's own host, not the referring page's origin, so this works from local
// dev too without any environment detection, and <img> tag loading isn't subject to CORS the
// way fetch()/canvas reads are.
export const SUPABASE_PUBLIC_PREFIX =
  'https://dhpifehbegozqmcrwlsw.supabase.co/storage/v1/object/public/';

export interface ProxyImageOptions {
  /** Target display width in CSS px (the worker clamps and never upscales past 800). */
  width?: number;
  /** JPEG/WebP/AVIF quality, 1–100. Defaults to the worker's own default when omitted. */
  quality?: number;
}

// image-proxy-worker (img.veevoerwinkel.co.za) now applies Cloudflare Image Resizing
// when width/quality query params are present — see its Worker source. When
// Transformations is disabled on the zone the params are ignored and the original
// 800×800 is served, so passing them is always safe.
export function toProxiedImageUrl(url: string, opts: ProxyImageOptions = {}): string {
  if (!url || !url.startsWith(SUPABASE_PUBLIC_PREFIX)) return url;
  const pathAndQuery = url.slice(SUPABASE_PUBLIC_PREFIX.length);
  const proxied = `https://img.veevoerwinkel.co.za/${pathAndQuery}`;

  const params = new URLSearchParams();
  if (opts.width && Number.isFinite(opts.width)) {
    params.set('width', String(Math.round(opts.width)));
  }
  if (opts.quality && Number.isFinite(opts.quality)) {
    params.set('quality', String(Math.round(opts.quality)));
  }
  const qs = params.toString();
  if (!qs) return proxied;
  return proxied + (proxied.includes('?') ? '&' : '?') + qs;
}

export function lazyLoadImage(imageElement: HTMLImageElement, src: string): void {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        imageElement.src = src;
        observer.disconnect();
      }
    });
  });
  
  observer.observe(imageElement);
}

/**
 * Validates image file before upload
 * 
 * @param file The file to validate
 * @param maxSizeMB Maximum file size in MB
 * @returns Validation result
 */
export function validateImageFile(file: File, maxSizeMB: number = 5): {
  valid: boolean;
  error?: string;
} {
  // Check file type
  if (!file.type.startsWith('image/')) {
    return { valid: false, error: 'File must be an image' };
  }
  
  // Check file size
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return { valid: false, error: `File size must be less than ${maxSizeMB}MB` };
  }
  
  // Check image dimensions (optional)
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      // Minimum dimensions check
      if (img.width < 200 || img.height < 200) {
        resolve({ valid: false, error: 'Image must be at least 200x200 pixels' });
      } else {
        resolve({ valid: true });
      }
    };
    img.onerror = () => {
      resolve({ valid: false, error: 'Invalid image file' });
    };
    img.src = URL.createObjectURL(file);
  }) as any;
}

/**
 * Gets the best available image URL, preferring SEO-friendly URLs
 */
export function getBestImageUrl(image: { 
  image_url: string; 
  seo_image_url?: string;
  product_id?: string; 
  seo_image_name?: string;
}): string {
  // First priority: Direct SEO URL (from migration script)
  if (image.seo_image_url) {
    return image.seo_image_url;
  }
  
  // Second priority: Construct SEO URL from seo_image_name (for future uploads)
  if (image.seo_image_name && image.product_id) {
    const base = 'https://dhpifehbegozqmcrwlsw.supabase.co/storage/v1/object/public/product-images';
    return `${base}/${image.seo_image_name}`;
  }
  
  // Fallback: Original image URL
  return image.image_url || '/placeholder.svg';
}

// Helper function for optimization with Supabase image transformation
export function getOptimizedImageUrl(
  url: string, 
  size: keyof ImageSizeConfig | number, 
  quality: number
): string {
  // If it's a Supabase storage URL, add transformation parameters
  if (url.includes('supabase.co/storage/v1/object/public/')) {
    const sizeValue = typeof size === 'number' ? size : getSizeValue(size);
    // Add Supabase image transformation parameters for better performance
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}width=${sizeValue}&quality=${quality}&format=webp`;
  }
  
  // For external URLs or when transformation isn't available, return as-is
  return url;
}

// Convert size config to pixel value
function getSizeValue(size: keyof ImageSizeConfig): number {
  const sizeMap: ImageSizeConfig = {
    thumbnail: 150,
    small: 250,
    medium: 400,
    large: 600,
    main: 800
  };
  return sizeMap[size] || 250;
}

// Build display URL with SEO-friendly filename if available
export function getDisplayImageUrl(
  image: { 
    image_url: string; 
    seo_image_url?: string;
    product_id?: string; 
    seo_image_name?: string;
  },
  size: keyof ImageSizeConfig | number = 'thumbnail',
  quality: number = 80
): string {
  // Get the best available URL
  const bestUrl = getBestImageUrl(image);
  
  // If it's already a SEO URL (from migration or direct), use it as-is
  // SEO URLs are already optimized with proper names
  if (image.seo_image_url || (image.seo_image_name && bestUrl.includes(image.seo_image_name))) {
    return bestUrl;
  }
  
  // For old URLs, apply optimization if available
  return getOptimizedImageUrl(bestUrl, size, quality);
}

// Convenience helper used across the app (keeps backward-compatibility)
export function getMainProductImageUrl(
  images: Array<{
    image_url: string;
    seo_image_url?: string;
    product_id?: string;
    seo_image_name?: string;
    image_order?: number;
  }>,
  size: keyof ImageSizeConfig | number = 'large',
  quality: number = 80
): string {
  if (!images || images.length === 0) return '/placeholder.svg';
  const ordered = [...images].sort((a, b) => (a.image_order ?? 9999) - (b.image_order ?? 9999));
  const image = ordered[0];
  const url = getDisplayImageUrl(image, size, quality);
  return url || image.image_url || '/placeholder.svg';
}